/**
 * CONTACT FORM
 *
 * @returns {Promise<boolean>}
 */
import moment from 'moment-timezone';
const main = async () => {
    const [
        { default: Vue },
        {ModalPlugin},
        {AjaxMixin},
        {GtmSiteMixin},
        {en, de, nl},
        ErrorMessages,
        {ValidationProvider, ValidationObserver, extend, configure, localize},
        {required, required_if, email, alpha_num, min_value, max_value, numeric, min, integer},
        {emailTld},
        VueScrollTo,
    ] = await Promise.all([
        import(/* webpackChunkName: "vue" */ 'vue'),
        import(/* webpackChunkName: "bs-modal" */ 'bootstrap-vue'),
        import(/* webpackChunkName: "global-mixins-ajax" */ '@mymyMixins/ajax'),
        import(/* webpackChunkName: "site-mixins-gtm" */ '@/js/site/mixins/gtm'),
        import(/* webpackChunkName: "vuejs-datepicker-locales" */ 'vuejs-datepicker/dist/locale'),
        import(/* webpackChunkName: "global-translations-validation" */ '@mymyTranslations/validation'),
        import(/* webpackChunkName: "vee-validate" */ 'vee-validate'),
        import(/* webpackChunkName: "vee-validate-rules" */ 'vee-validate/dist/rules'),
        import(/* webpackChunkName: "global-validators" */ '@mymyValidators'),
        import(/* webpackChunkName: "vue-scrollto" */ 'vue-scrollto'),
    ]);

    Vue.use(ModalPlugin);

    // for scrolling 
    Vue.use(VueScrollTo, {
        offset: -110,
    });

    // load our translated form error messages for this page's language
    localize(window.language, ErrorMessages.default[window.language]);

    // add our Vee Validate validators (email is already loaded)
    extend('required', required);
    extend('required_if', required_if);
    extend('alpha_num', alpha_num);
    extend('min_value', min_value);
    extend('max_value', max_value);
    extend('min', min);
    extend('email', email);
    extend('emailTld', emailTld);
    extend('numeric', numeric);
    extend('integer', integer);

    // tell Vee Validate which classes to apply when a field is valid/invalid, etc
    configure({ classes: { invalid: 'is-invalid' } });


    const vm = new Vue({
        name: 'ContactFormMatrixBlock',
        el: '#contact-form',
        delimiters: ['[[', ']]'],
        mixins: [AjaxMixin, GtmSiteMixin],
        components: {
            'validation': ValidationProvider,
            'validation-observer': ValidationObserver,
            'datepicker': () => import(/* webpackChunkName: "vuejs-datepicker" */ 'vuejs-datepicker'),
        },
        data: {
            fields: {},
            success: false,
            isLoading: false,
            serverErrors: [],
            optionsWithOrderNumber: [
                'existingOrder',
                'invoice',
                'complaint',
            ],
            // languages for date picker form field
            de: de,
            en: en,
            nl: nl,
        },
        created() {
            let wrapper = document.getElementById('contact-form');
            this.fields = JSON.parse(wrapper.dataset.fields);

            // fetch any field values we can pre-populate the form with
            let urlParams = new URLSearchParams(window.location.search);

            // get values we already have for the form inputs
            this.makeGetRequest('mymy/forms/contact-form-values', {
                params: {
                    number: urlParams.get('number'),
                    topic: urlParams.get('topic'),
                }},
                true
            ).then((response) => this.fields = response?.data?.fields);
        },

        computed: {
            /**
             * COMPUTED: Should the form show the 'orderNumber' field?
             *
             * @returns {boolean}
             */
            needsOrderNumber() {
                return this.optionsWithOrderNumber.includes(this.fields.topic);
            },

            /**
             * COMPUTED: Should the form show the fields related to requesting an offer?
             */
            showRequestOfferFields() {
                return this.fields.topic === 'requestOffer';
            },

            /**
             * COMPUTED: Get the date the datepicker field opens to.
             *
             * @returns {Date}
             */
            datepickerOpenDate() {
                return moment(new Date()).add(3,'days').toDate();
            },

            /**
             * COMPUTED: Get the dates that are disabled in the datepicker field.
             *
             * @returns {{to: Date, from: Date}}
             */
            disabledDates() {
                return {
                    to: moment(new Date()).add(2,'days').toDate(),
                    from: moment(new Date()).add(1,'year').toDate()
                }
            },
        },

        methods: {

            // EVENTS
            // =====================================================================================

            /**
             * METHOD: Handle the form submit.
             */
            async onSubmit() {
                this.isLoading = true;

                // check the honey pot for values
                if (! this.passesHoneyPotTest()) {
                    this.success = true;
                    this.resetForm();
                    return;
                }

                // send the data to our controller (and include any extra data from the URL like UTM
                // params for marketing and tracking)
                const response = await this.makePostRequest(
                   'mymy/forms/submit-contact-form' + window.location.search,
                   {
                       fields: this.fields,
                       title: this.$refs.title.value
                   }
                );

                if (response?.data?.success) {
                    this.success = true;

                    // tell google tag manager about the submission
                    this.gtmContactFormSubmit({
                        topic: this.fields.topic,
                        message: this.fields.message,
                    });

                    if (window.posthog) {
                        window.posthog.capture('user_contact_form_submit', {
                            topic: this.fields.topic,
                        })
                    }

                    this.resetForm();
                }
                else if (response?.data?.error) {
                    this.success = false;
                    this.logErrors({message: [response.data.error]});
                }
                else if (response?.data?.errors) {
                    this.success = false;
                    this.logErrors(response.data.errors);
                }
                else {
                    this.success = false;
                    this.logErrors({message: ["There was an error"]});
                    if(window.Sentry) {
                        Sentry.captureMessage("Response not handled correctly")
                    }
                }
            },



            // HELPERS
            // =====================================================================================

            /**
             * METHOD: Reset the form to have no values in the fields.
             */
            resetForm() {
                for (let field in this.fields) {
                    this.fields[field] = '';
                }
                this.$refs.observer.reset();
                this.isLoading = false;
            },

            /**
             * METHOD: Handle Yii's errors coming back from the server.
             *
             * @param errors
             */
            logErrors(errors) {
                this.serverErrors = [];
                for (const erroredField in errors) {
                    for (const errorMessage of errors[erroredField]) {
                        this.serverErrors.push(errorMessage);
                    }
                }
                this.isLoading = false;
            },

            /**
             * METHOD: Check if our "honey pot" field has a value.
             *
             * @returns {boolean}
             */
            passesHoneyPotTest() {
                return this.$refs.title.value === '';
            },

            /**
             * METHOD: How should the datepicker field show its date?
             *
             * @param date
             * @returns {string}
             */
            customDatepickerFormatter(date) {
                return moment(date).format('D. MMM YYYY');
            },
        },
    });

    return vm;
};

// Execute async function
main().then((vm) => {
});



// Accept HMR as per: https://webpack.js.org/api/hot-module-replacement#accept
if (module.hot) {
    module.hot.accept();
}